import * as THREE from 'three';
import { WebGLElement } from './base.js';

const TYPES = {
  ambient: (color, intensity) => new THREE.AmbientLight(color, intensity),
  directional: (color, intensity) => new THREE.DirectionalLight(color, intensity),
  point: (color, intensity) => new THREE.PointLight(color, intensity),
  hemisphere: (color, intensity) => new THREE.HemisphereLight(color, 0x444444, intensity),
};

export class ThreeLight extends WebGLElement {
  static properties = {
    ...WebGLElement.properties,
    type: { type: String },
    color: { type: String },
    intensity: { type: Number },
    castShadow: { type: Boolean, attribute: 'cast-shadow' },
    shadowSize: { type: Number, attribute: 'shadow-size' },
    shadowMapSize: { type: Number, attribute: 'shadow-map-size' },
  };

  constructor() {
    super();
    this.type = 'ambient';
    this.color = '#ffffff';
    this.intensity = 1;
    this.castShadow = false;
    this.shadowSize = 80;
    this.shadowMapSize = 2048;
  }

  _configureShadow() {
    const light = this.object3D;
    if (!light?.shadow) return;
    light.castShadow = !!this.castShadow;
    if (!this.castShadow) return;
    light.shadow.mapSize.set(this.shadowMapSize, this.shadowMapSize);
    if (light.shadow.camera.isOrthographicCamera) {
      const s = this.shadowSize;
      light.shadow.camera.left = -s;
      light.shadow.camera.right = s;
      light.shadow.camera.top = s;
      light.shadow.camera.bottom = -s;
      light.shadow.camera.near = 0.1;
      light.shadow.camera.far = s * 4;
      light.shadow.camera.updateProjectionMatrix();
    }
    light.shadow.bias = -0.0005;
  }

  createObject3D() {
    const factory = TYPES[this.type] || TYPES.ambient;
    return factory(new THREE.Color(this.color), this.intensity);
  }

  updated(changed) {
    super.updated(changed);
    if (changed.has('color') && this.object3D.color) {
      this.object3D.color.set(this.color);
    }
    if (changed.has('intensity')) {
      this.object3D.intensity = this.intensity;
    }
    if (changed.has('type')) {
      const oldObj = this.object3D;
      const parent = oldObj.parent;
      this.object3D = this.createObject3D();
      this.applyTransform();
      if (parent) {
        parent.remove(oldObj);
        parent.add(this.object3D);
      }
    }
    this._configureShadow();
  }

  beforeAttach() {
    super.beforeAttach();
    this._configureShadow();
  }
}

customElements.define('three-light', ThreeLight);
